import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Rocket, X, Check, Wallet, BadgeCheck, TrendingUp, Clock, ChevronUp, ChevronDown } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useTrustScore } from '../hooks/useTrustScore';
import { canPurchaseBoost } from '../lib/trustScore';
import {
  BOOST_WALLET, BOOST_KIND, BOOST_D_TAG_PREFIX, BOOST_TAG,
  BOOST_DURATION, BOOST_MIN_BID_USD, BOOST_BID_PRESETS, BOOST_SLOT_POSITIONS,
  usdToSats, satsToUsdStr, getActivePubkeyBoosts, getAllActiveBoostsForPubkey,
  getActiveBoostLeaderboard, type BoostData,
} from '../lib/boost';
import { useNostrQuery } from '../hooks/useNostrQuery';

interface BoostPostModalProps {
  eventId: string;
  onClose: () => void;
  onBoostSuccess?: () => void;
}

export default function BoostPostModal({ eventId, onClose, onBoostSuccess }: BoostPostModalProps) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const trust = useTrustScore(user?.pubkey);
  const boostEligibility = canPurchaseBoost(trust);
  const [bidUsd, setBidUsd] = useState(1); // Default $1 bid
  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const [boostError, setBoostError] = useState<string | null>(null);
  const [boostSuccess, setBoostSuccess] = useState(false);
  const [walletInitializing, setWalletInitializing] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const bidSats = usdToSats(bidUsd, bchPrice);
  const minBidSats = usdToSats(BOOST_MIN_BID_USD, bchPrice);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = bidSats !== null && walletBalance >= bidSats;
  const bidTooLow = bidUsd < BOOST_MIN_BID_USD;

  // Fetch all current boosts to show the leaderboard
  const [boostSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 2);
  const { events: allBoostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [BOOST_TAG], since: boostSince, limit: 50 }],
    enabled: true,
    allRelays: true,
  });

  // Current leaderboard — what you're competing against
  const leaderboard = useMemo(
    () => getActiveBoostLeaderboard(allBoostEvents, bchPrice),
    [allBoostEvents, bchPrice],
  );

  // Where would the user's bid land?
  const projectedRank = useMemo(() => {
    if (!bidSats) return leaderboard.length + 1;
    const position = leaderboard.findIndex(b => bidSats > b.bidSats);
    return position >= 0 ? position + 1 : leaderboard.length + 1;
  }, [bidSats, leaderboard]);

  const projectedSlot = projectedRank <= BOOST_SLOT_POSITIONS.length
    ? BOOST_SLOT_POSITIONS[projectedRank - 1]
    : null;

  // Check if this user already has an active boost for THIS SPECIFIC POST.
  // Users CAN boost multiple different posts simultaneously — only block re-boosting the same one.
  const { events: myBoostEvents } = useNostrQuery({
    filters: user ? [{ kinds: [BOOST_KIND], authors: [user.pubkey], since: boostSince, limit: 20 }] : [],
    enabled: !!user,
    allRelays: true,
  });
  const existingBoostForThisPost = useMemo(() => {
    if (!user) return null;
    const allActive = getAllActiveBoostsForPubkey(myBoostEvents, user.pubkey);
    return allActive.get(eventId) ?? null;
  }, [myBoostEvents, user?.pubkey, eventId]);
  const alreadyBoosting = existingBoostForThisPost !== null;

  // Minimum bid to beat the current lowest visible boost
  const lowestVisibleBid = leaderboard.length >= BOOST_SLOT_POSITIONS.length
    ? leaderboard[leaderboard.length - 1]
    : null;

  const handlePreset = (usd: number) => {
    setBidUsd(usd);
    setShowCustom(false);
    setCustomInput('');
  };

  const handleCustomInput = (val: string) => {
    setCustomInput(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      setBidUsd(Math.round(num * 100) / 100);
    }
  };

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handleBoost = async () => {
    if (!user || !canAfford || bidSats === null || bidTooLow) return;
    // Enforce eligibility — prevent boosting even if UI was bypassed
    if (!boostEligibility.eligible) return;
    setBoosting(true);
    setBoostError(null);

    const now = Math.floor(Date.now() / 1000);
    const boostData: BoostData = {
      eventId,
      tier: 'bid',
      priceSats: bidSats,
      expiresAt: now + BOOST_DURATION,
      boostedBy: user.pubkey,
      bidUsd,
    };

    // ── Step 1: Publish boost event to Nostr FIRST ──
    // This must succeed before we send any BCH. If the signer denies
    // or relays fail, the user hasn't lost any money.
    let publishedOk = false;
    try {
      await publish({
        kind: BOOST_KIND,
        content: JSON.stringify(boostData),
        tags: [
          ['d', BOOST_D_TAG_PREFIX + eventId],
          ['t', BOOST_TAG],
          ['t', 'bch'],
          ['e', eventId],
          ['expires', String(now + BOOST_DURATION)],
          ['amount', String(bidSats)],
        ],
        created_at: now,
      });
      publishedOk = true;
    } catch (e) {
      setBoosting(false);
      setBoostError(
        e instanceof Error && (e.message.includes('denied') || e.message.includes('reject'))
          ? 'Signing was denied. No BCH was charged.'
          : 'Failed to publish boost event. No BCH was charged. Please try again.'
      );
      return;
    }

    // ── Step 2: Send BCH payment ──
    // The boost event is already on Nostr, so even if payment fails
    // the boost will still show (user can retry payment separately).
    try {
      const txId = await tipWallet.sendBCH(BOOST_WALLET, BigInt(bidSats));

      // Re-publish with txid included (best-effort, non-blocking)
      publish({
        kind: BOOST_KIND,
        content: JSON.stringify({ ...boostData }),
        tags: [
          ['d', BOOST_D_TAG_PREFIX + eventId],
          ['t', BOOST_TAG],
          ['t', 'bch'],
          ['e', eventId],
          ['expires', String(now + BOOST_DURATION)],
          ['amount', String(bidSats)],
          ['txid', txId],
        ],
        created_at: now + 1, // bump timestamp so it replaces the previous event
      }).catch(() => {}); // don't block on this

      setBoostSuccess(true);
      tipWallet.refreshBalance();
      // Notify parent to refresh the feed so the boost appears immediately
      onBoostSuccess?.();
      // Also dispatch a global event so the feed (Index page) can refresh boost queries
      window.dispatchEvent(new CustomEvent('bchnostr:boost-published', { detail: { eventId } }));
    } catch (e) {
      // Payment failed but boost event is already published.
      // Show partial success — the boost is live, payment needs retry.
      if (publishedOk) {
        setBoostSuccess(true); // boost event is on Nostr, it will show in feed
        setBoostError('Note: Payment may not have completed. Your boost is live but please check your wallet balance.');
        onBoostSuccess?.(); // still trigger feed refresh — boost event IS published
        window.dispatchEvent(new CustomEvent('bchnostr:boost-published', { detail: { eventId } }));
      } else {
        setBoostError(e instanceof Error ? e.message : 'Payment failed');
      }
    } finally {
      setBoosting(false);
    }
  };

  // Render through a portal to document.body. PostCard lives inside
  // .feed-post-contain (content-visibility/contain), which makes a
  // `position: fixed` child resolve against the CARD, not the viewport —
  // clipping the modal so its menu appears hidden/cut off. Portaling to
  // <body> escapes that containment, exactly like BCHTipButton does.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-2">
          <h2 className="font-bold text-base flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            Boost Post
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 pb-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {trust.loading ? (
            <div className="text-center py-10 space-y-3">
              <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
              <p className="text-xs text-muted-foreground">Checking eligibility...</p>
            </div>
          ) : !boostEligibility.eligible ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                <BadgeCheck className="w-7 h-7 text-primary" />
              </div>
              <p className="text-sm font-bold">Verification Required</p>
              <p className="text-xs text-muted-foreground leading-relaxed px-2">
                {boostEligibility.reason}
              </p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors">
                Got it
              </button>
            </div>
          ) : alreadyBoosting ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
                <Rocket className="w-7 h-7 text-amber-500" />
              </div>
              <p className="text-sm font-bold">This post is already boosted</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your boost on this post expires in{' '}
                <span className="font-semibold text-foreground">
                  {(() => {
                    const remaining = Math.max(0, existingBoostForThisPost!.expiresAt - Math.floor(Date.now() / 1000));
                    if (remaining <= 0) return 'soon';
                    const h = Math.floor(remaining / 3600);
                    const m = Math.floor((remaining % 3600) / 60);
                    return h > 0 ? `${h}h ${m}m` : `${m}m`;
                  })()}
                </span>
                . You can re-boost this post once it expires, or boost a different post now.
              </p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-muted text-foreground text-sm font-medium hover:bg-muted/80 transition-colors">
                Got it
              </button>
            </div>
          ) : boostSuccess ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-emerald-500" />
              </div>
              <p className="text-sm font-bold text-emerald-500">Post Boosted!</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This post will appear in the feed for the next 6 hours.
                {projectedRank <= 3 && (
                  <span className="block mt-1 font-semibold text-amber-500">
                    Your bid landed at #{projectedRank} — prime placement!
                  </span>
                )}
              </p>
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Explanation */}
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Name your price. Higher bids get better placement in the feed. All boosts last <strong className="text-foreground">6 hours</strong>.
                </p>
              </div>

              {/* ── Bid Amount Section ──────────────────────── */}
              <div className="space-y-3">
                <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5 text-primary" />
                  Your Bid
                </label>

                {/* Preset buttons */}
                <div className="flex gap-1.5 flex-wrap">
                  {BOOST_BID_PRESETS.map(p => (
                    <button
                      key={p.usd}
                      onClick={() => handlePreset(p.usd)}
                      className={`flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                        bidUsd === p.usd && !showCustom
                          ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                          : 'border-border hover:border-primary/40 text-foreground'
                      }`}
                    >
                      <span>{p.emoji}</span>
                      <span>{p.label}</span>
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCustom(!showCustom)}
                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                      showCustom
                        ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary/30'
                        : 'border-border hover:border-primary/40 text-muted-foreground'
                    }`}
                  >
                    Custom
                  </button>
                </div>

                {/* Custom input */}
                {showCustom && (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">$</span>
                      <input
                        type="number"
                        min={BOOST_MIN_BID_USD}
                        step="0.05"
                        value={customInput}
                        onChange={e => handleCustomInput(e.target.value)}
                        placeholder={`Min $${BOOST_MIN_BID_USD.toFixed(2)}`}
                        className="w-full pl-7 pr-3 py-2.5 rounded-xl border border-border bg-muted/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                        autoFocus
                      />
                    </div>
                  </div>
                )}

                {/* Bid summary */}
                <div className="rounded-xl bg-gradient-to-br from-amber-500/5 to-orange-500/5 border border-amber-500/20 p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Your bid</span>
                    <div className="text-right">
                      <span className="text-sm font-bold text-foreground">${bidUsd.toFixed(2)}</span>
                      <span className="text-[10px] text-muted-foreground font-mono ml-1.5">
                        {bidSats ? `~${bidSats.toLocaleString()} sats` : '...'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Duration</span>
                    <span className="text-xs font-semibold text-foreground">6 hours</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Projected rank</span>
                    <span className={`text-xs font-bold ${
                      projectedRank === 1 ? 'text-amber-500' :
                      projectedRank <= 3 ? 'text-primary' :
                      projectedRank <= BOOST_SLOT_POSITIONS.length ? 'text-foreground' :
                      'text-muted-foreground'
                    }`}>
                      #{projectedRank}
                      {projectedSlot && (
                        <span className="text-muted-foreground font-normal ml-1">
                          · appears at position {projectedSlot} in feed
                        </span>
                      )}
                    </span>
                  </div>
                  {bidTooLow && (
                    <p className="text-[10px] text-red-500 font-medium">
                      Minimum bid is ${BOOST_MIN_BID_USD.toFixed(2)}
                    </p>
                  )}
                  {lowestVisibleBid && bidSats !== null && bidSats < lowestVisibleBid.bidSats && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                      All {BOOST_SLOT_POSITIONS.length} slots are taken. Bid at least {lowestVisibleBid.bidUsd || `${lowestVisibleBid.bidSats.toLocaleString()} sats`} to get a slot.
                    </p>
                  )}
                </div>
              </div>

              {/* ── Current Bids Leaderboard ─────────────────── */}
              {leaderboard.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowLeaderboard(!showLeaderboard)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors w-full"
                  >
                    {showLeaderboard ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    Current bids ({leaderboard.length} active)
                  </button>
                  {showLeaderboard && (
                    <div className="mt-2 rounded-xl border border-border overflow-hidden">
                      {leaderboard.map((b, i) => {
                        const remaining = b.expiresAt - Math.floor(Date.now() / 1000);
                        const h = Math.floor(remaining / 3600);
                        const m = Math.floor((remaining % 3600) / 60);
                        const timeLeft = h > 0 ? `${h}h ${m}m` : `${m}m`;
                        return (
                          <div key={b.eventId} className="flex items-center justify-between px-3 py-2 border-b border-border last:border-0">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-extrabold w-5 text-center ${
                                i === 0 ? 'text-amber-500' : i === 1 ? 'text-slate-400' : i === 2 ? 'text-amber-700' : 'text-muted-foreground'
                              }`}>
                                #{b.rank}
                              </span>
                              <span className="text-xs font-bold text-foreground">
                                {b.bidUsd || `${b.bidSats.toLocaleString()} sats`}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                              <Clock className="w-2.5 h-2.5" />
                              {timeLeft}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {leaderboard.length === 0 && (
                <div className="rounded-xl bg-emerald-500/5 border border-emerald-500/20 px-3 py-2.5">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1.5">
                    <Rocket className="w-3 h-3" />
                    No active boosts — your post will be #1!
                  </p>
                </div>
              )}

              {/* ── Wallet ────────────────────────────────────── */}
              {!tipWallet.ready ? (
                <div className="rounded-xl bg-muted/50 border border-border p-3 text-center">
                  <p className="text-xs text-muted-foreground mb-2">Initialize your wallet to boost</p>
                  <button
                    onClick={handleInitWallet}
                    disabled={walletInitializing}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 mx-auto"
                  >
                    {walletInitializing ? (
                      <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Wallet className="w-3.5 h-3.5" />
                    )}
                    {walletInitializing ? 'Loading...' : 'Load Wallet'}
                  </button>
                </div>
              ) : (
                <div className="rounded-xl bg-muted/50 border border-border p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Wallet Balance</span>
                    <span className={`text-xs font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
                      {satsToUsdStr(walletBalance, bchPrice) || `${walletBalance.toLocaleString()} sats`}
                    </span>
                  </div>
                  {!canAfford && bidSats !== null && (
                    <p className="text-[10px] text-red-500 mt-1">
                      Not enough funds. Need {satsToUsdStr(bidSats - walletBalance, bchPrice) || `${(bidSats - walletBalance).toLocaleString()} sats`} more.
                      Deposit to your tip wallet in Settings.
                    </p>
                  )}
                </div>
              )}

              {/* Price feed unavailable — explain instead of a silent dead button */}
              {bchPrice === null && (
                <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 px-3 py-2.5">
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-relaxed">
                    Can't reach the BCH price right now, so we can't convert your bid to sats.
                    Please check your connection and try again in a moment.
                  </p>
                </div>
              )}

              {/* ── Boost button ──────────────────────────────── */}
              <button
                onClick={handleBoost}
                disabled={!tipWallet.ready || !canAfford || boosting || bidTooLow || !boostEligibility.eligible}
                className={`w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                  tipWallet.ready && canAfford && !bidTooLow
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:opacity-90 active:scale-[0.98]'
                    : 'bg-muted text-muted-foreground cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {boosting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Rocket className="w-4 h-4" />
                )}
                {boosting ? 'Boosting...' : `Boost for $${bidUsd.toFixed(2)}`}
              </button>

              {boostError && <p className="text-xs text-red-500 text-center">{boostError}</p>}

              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                Higher bids get better feed placement. Your bid competes with other active boosts for the best slots.
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

